import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import _noop from 'lodash/noop';
import React, { Children, Component } from 'react';
import ThemeProvider from '@sitebase/theme-context/es/ThemeProvider';

import ReactDOM from 'react-dom';
import { childrenOf, withShape } from 'airbnb-prop-types';
import PropTypes from 'prop-types';


import HeaderDesktop from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';
import { getChildrenAsArray, hasSlot } from '../utils';
import HeaderContext from '../Layout/HeaderContext';

export var HEADER_DESKTOP_TYPE = 'desktop';
export var HEADER_MOBILE_TYPE = 'mobile';

var combineConfiguration = function combineConfiguration(headerConfigFromLayout, configFromProps) {
  var chooseValueAndLogWarning = function chooseValueAndLogWarning(propName) {
    var layoutValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var headerValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    var defaultValue = arguments[3];

    if (process.env.NODE_ENV !== 'production') {
      if (layoutValue && headerValue) {
        /* eslint-disable-next-line no-console */
        console.warn('Prop ' + propName + ' provided to the Header is ignored because Layout is taking control over it when used along with Header');
      }
    }
    return layoutValue || headerValue || defaultValue || null;
  };

  var makeHandler = function makeHandler() {
    var layoutCallback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _noop;
    var headerCallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _noop;
    return function () {
      headerCallback.apply(undefined, arguments);
      layoutCallback.apply(undefined, arguments);
    };
  };

  return {
    type: chooseValueAndLogWarning('type', headerConfigFromLayout.type, configFromProps.type, HEADER_MOBILE_TYPE),
    collapsed: chooseValueAndLogWarning('collapsed', headerConfigFromLayout.collapsed, configFromProps.collapsed),
    shifted: chooseValueAndLogWarning('shifted', headerConfigFromLayout.shifted, configFromProps.shifted),
    hamburgerState: chooseValueAndLogWarning('hamburgerState', headerConfigFromLayout.hamburgerState, configFromProps.hamburgerState, 'inactive'),
    onSearchOpen: makeHandler(headerConfigFromLayout.onSearchOpen, configFromProps.onSearchOpen),
    onSearchClose: makeHandler(headerConfigFromLayout.onSearchClose, configFromProps.onSearchClose),
    onHamburgerClick: makeHandler(headerConfigFromLayout.onHamburgerClick, configFromProps.onHamburgerClick),
    subNavCustom: configFromProps.subNavCustom
  };
};

/* eslint-disable-next-line react/prefer-stateless-function */

var Header = function (_Component) {
  _inherits(Header, _Component);

  function Header(props, context) {
    _classCallCheck(this, Header);

    var _this = _possibleConstructorReturn(this, (Header.__proto__ || _Object$getPrototypeOf(Header)).call(this, props, context));

    _this.storeActionAreaRef = function (ref) {
      _this.ref.actionArea = ref;
    };

    _this._renderHeader = function (headerConfigFromLayout, headerConfig) {
      var _this$props = _this.props,
          innerRef = _this$props.innerRef,
          searchOpen = _this$props.searchOpen,
          subNavCustom = _this$props.subNavCustom;
      var asideOpen = headerConfigFromLayout.asideOpen,
          openAside = headerConfigFromLayout.openAside,
          closeAside = headerConfigFromLayout.closeAside,
          setHeaderLayout = headerConfigFromLayout.setHeaderLayout;
      var subNavStart = headerConfig.subNavStart,
          subNavEnd = headerConfig.subNavEnd,
          info = headerConfig.info;

      var combinedConfiguration = combineConfiguration(headerConfigFromLayout, _this.props);
      var type = combinedConfiguration.type,
          collapsed = combinedConfiguration.collapsed,
          shifted = combinedConfiguration.shifted,
          hamburgerState = combinedConfiguration.hamburgerState,
          onHamburgerClick = combinedConfiguration.onHamburgerClick,
          onSearchClose = combinedConfiguration.onSearchClose,
          onSearchOpen = combinedConfiguration.onSearchOpen;


      _this.layoutAsideOpenValue = asideOpen;
      _this.layoutOpenAside = openAside;
      _this.layoutCloseAside = closeAside;

      innerRef(_this);

      if (typeof setHeaderLayout === 'function') {
        if (type === HEADER_DESKTOP_TYPE) {
          var headerBaseLayout = subNavStart || subNavEnd ? 'desktop-high' : 'desktop-low';
          setHeaderLayout(info ? headerBaseLayout + '-with-info' : headerBaseLayout);
        } else {
          setHeaderLayout(info ? 'mobile-with-info' : 'mobile');
        }
      }

      return type === HEADER_DESKTOP_TYPE ? React.createElement(
        HeaderContext.Provider,
        { value: combinedConfiguration },
        React.createElement(HeaderDesktop, {
          headerConfig: headerConfig,
          storeActionAreaRef: _this.storeActionAreaRef,
          subNavCustom: subNavCustom,
          searchOpen: searchOpen,
          onSearchOpen: onSearchOpen,
          onSearchClose: onSearchClose
        })
      ) : React.createElement(
        HeaderContext.Provider,
        { value: combinedConfiguration },
        React.createElement(HeaderMobile, {
          headerConfig: headerConfig,
          storeActionAreaRef: _this.storeActionAreaRef,
          searchOpen: searchOpen,
          onSearchOpen: onSearchOpen,
          onSearchClose: onSearchClose,
          collapsed: collapsed,
          shifted: shifted,
          hamburgerState: hamburgerState,
          onHamburgerClick: onHamburgerClick
        })
      );
    };

    _this.ref = {
      actionArea: null,
      header: null
    };

    _this.layoutAsideOpenValue = null;
    _this.layoutOpenAside = null;
    _this.layoutCloseAside = null;
    return _this;
  }

  _createClass(Header, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      // eslint-disable-next-line react/no-find-dom-node
      this.ref.header = ReactDOM.findDOMNode(this);
    }
  }, {
    key: 'render',
    value: function render() {
      var _this2 = this;

      var children = this.props.children;


      var headerSlots = Children.toArray(children);

      var logo = headerSlots.find(hasSlot(['logo']));
      var actions = headerSlots.find(hasSlot(['actions']));
      var productNav = headerSlots.find(hasSlot(['product-nav']));
      var subNavStart = headerSlots.find(hasSlot(['sub-nav-start']));
      var subNavEnd = headerSlots.find(hasSlot(['sub-nav-end']));
      var search = headerSlots.find(hasSlot(['search']));
      var info = headerSlots.find(hasSlot(['info']));

      var headerConfig = {
        logo: logo,
        actions: getChildrenAsArray(actions),
        productNav: productNav,
        subNavStart: subNavStart,
        subNavEnd: subNavEnd,
        search: search,
        info: info
      };

      /* eslint-disable no-underscore-dangle */
      return React.createElement(
        ThemeProvider,
        { theme: 'dark' },
        React.createElement(
          HeaderContext.Consumer,
          null,
          function (headerConfigFromLayout) {
            return _this2._renderHeader(headerConfigFromLayout, headerConfig);
          }
        )
      );
      /* eslint-enable */
    }
  }, {
    key: 'searchOpen',
    get: function get() {
      return this.ref.actionArea && this.ref.actionArea.open;
    },
    set: function set(value) {
      if (this.ref.actionArea) {
        this.ref.actionArea.open = value;
      }
    }
  }]);

  return Header;
}(Component);

Header.propTypes = {
  children: childrenOf(withShape(PropTypes.node, {
    props: PropTypes.shape({
      slot: PropTypes.oneOf(['product-nav', 'sub-nav-start', 'sub-nav-end', 'actions', 'search', 'logo', 'info'])
    })
  })),
  innerRef: PropTypes.func,
  subNavCustom: PropTypes.shape({
    color: PropTypes.string,
    background: PropTypes.string
  }),
  searchOpen: PropTypes.bool,
  /* eslint-disable react/no-unused-prop-types */
  onSearchOpen: PropTypes.func,
  onSearchClose: PropTypes.func,
  type: PropTypes.PropTypes.oneOf([HEADER_DESKTOP_TYPE, HEADER_MOBILE_TYPE]),
  collapsed: PropTypes.bool,
  shifted: PropTypes.bool,
  hamburgerState: PropTypes.oneOf(['active', 'inactive', 'hidden']),
  onHamburgerClick: PropTypes.func
  /* eslint-enable react/no-unused-prop-types */
};
Header.defaultProps = {
  innerRef: _noop
};
export default Header;
//# sourceMappingURL=Header.js.map